import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "app/rootReducer";
import config from "config";
import { fetchBookings } from "features/calendar/reducer";
import { View } from "features/global/reducer";
import { fetchDashboardFeed } from "features/home/reducer";
import { fetchUserProfile } from "features/user/reducer";
import io from "socket.io-client";
import { getAuthToken } from "./api";

// middleware for handling socket events
const socketMiddleware: Middleware<{}, RootState> = ({ dispatch, getState }) => {
    // connect to socket
    const socket = io(config.api.url);

    // socket connected
    socket.on("connect", async () => {
        // console.log("connected");
        socket.emit("authentication", { token: await getAuthToken() });
    });

    // socket disconnected
    socket.on("disconnect", () => {
        // console.log("disconnected");
        setTimeout(() => socket.connect(), 3000); // try to reconnect after 3s
    });

    // one or more bookings created
    socket.on("bookings-created", () => {
        // console.log("created", bookingIds);
        const clubId = getState().calendar.club!.id;
        if (getState().global.view === View.Home) {
            dispatch(fetchDashboardFeed(clubId, 0) as any);
        } else {
            dispatch(fetchBookings(clubId, getState().calendar.date) as any);
        }
    });

    // one or more properties of bookings changed
    socket.on("bookings-changed", () => {
        // console.log("changed", bookingIds);
        const clubId = getState().calendar.club!.id;
        if (getState().global.view === View.Home) {
            dispatch(fetchDashboardFeed(clubId, 0) as any);
        } else {
            dispatch(fetchBookings(clubId, getState().calendar.date) as any);
        }
    });

    // story created in a club
    socket.on("story-created", () => {
        const clubId = getState().calendar.club!.id;
        dispatch(fetchDashboardFeed(clubId, 0) as any);
    });

    // story deleted in a club
    socket.on("story-deleted", () => {
        const clubId = getState().calendar.club!.id;
        dispatch(fetchDashboardFeed(clubId, 0) as any);
    });

    // owner identity verification
    socket.on("club-verification-status-changed", () => {
        dispatch(fetchUserProfile() as any);
    });

    // debug
    // socket.on("authenticated", (e: any) => console.log("authenticated", e));
    // socket.on("error", (e: any) => console.log("error", e));
    // socket.on("unauthorized", (e: any) => console.log("unauthorized", e));
    // socket.on("api-error", (e: any) => console.log("api-error", e));

    // action listener
    return (next) => (action) => {
        // update auth state by reconnecting
        if (action.type === "auth/authSuccess") {
            socket.disconnect();
            socket.connect();
        }

        // subscribe to bookings
        let result;
        if (action.type === "calendar/getBookingsSuccess") {
            const oldIds = getState().calendar.bookings.map((b) => b.id);
            result = next(action);
            const newIds = getState().calendar.bookings.map((b) => b.id);
            socket.emit("bulk-unsubscribe-from-rooms", oldIds.filter((id) => !newIds.includes(id)));
            socket.emit("bulk-subscribe-to-rooms", newIds.filter((id) => !oldIds.includes(id)));
        } else if (action.type === "home/getFeedSuccess") {
            const oldIds = getState().home.feedItems.filter((f) => f.type === "booking").map((f) => f.booking!.id);
            result = next(action);
            const newIds = getState().home.feedItems.filter((f) => f.type === "booking").map((f) => f.booking!.id);
            socket.emit("bulk-unsubscribe-from-rooms", oldIds.filter((id) => !newIds.includes(id)));
            socket.emit("bulk-subscribe-to-rooms", newIds.filter((id) => !oldIds.includes(id)));
        } else {
            result = next(action);
        }

        return result;
    };
};

export default socketMiddleware;
